import { debounce, noop } from 'lodash/fp';
import { s16, s32, s8 } from '@ardoq/design-tokens';
import { smartSort } from '@ardoq/pagination';
import { RefObject, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Filter,
  TablesSelectionBaseTable,
  TablesSelectionTablePayload,
  TablesSelectionTexts,
} from './types';
import { header3, Paragraph, text2 } from '@ardoq/typography';
import { TableSearch, filterTables } from './TableSearch';
import { TablesTable } from './TablesTable';
import { getDefaultTexts } from './texts';
import { useSortableTable } from '@ardoq/table-legacy';
import { FlexBox } from '@ardoq/layout';
import { IconName } from '@ardoq/icons';
import { SegmentedControl } from '@ardoq/button';
import { type I18nLocale, t } from '@ardoq/i18n';

const Header = styled.div`
  ${header3}
  padding: ${s16} 0;
`;

const Text = styled.div`
  ${text2}
  padding-bottom: ${s32};
`;

type SelectedTable = Record<string, unknown>;

type TableParams<T extends TablesSelectionTablePayload> = {
  i18nLocale: I18nLocale;
  dataSource: T[];
  onSelectTable?: (table: TablesSelectionBaseTable) => void;
  selectedRowId: string | null;
  selectedTables: Record<string, SelectedTable>;
  disabled?: boolean;
  selectionRef?: RefObject<HTMLDivElement>;
  texts?: TablesSelectionTexts;
  hasPreselectedConfig?: boolean;
  allFilterLabel?: string;
  addedFilterLabel?: string;
  noResultsTitle?: string;
  noResultsDescription?: string;
};

const TablesContainer = styled.div`
  margin-bottom: ${s8};
`;

const FilterContainer = styled.div`
  width: 208px;
`;

export const TablesSelection = <T extends TablesSelectionTablePayload>({
  i18nLocale,
  dataSource,
  onSelectTable = noop,
  selectedRowId,
  selectedTables,
  disabled,
  selectionRef,
  texts = getDefaultTexts(i18nLocale),
  hasPreselectedConfig = false,
  allFilterLabel = t(
    i18nLocale,
    'integrations:tablesSelection.allFilterLabel',
    'All'
  ),
  addedFilterLabel = t(
    i18nLocale,
    'integrations:tablesSelection.addedFilterLabel',
    'Added'
  ),
  noResultsTitle,
  noResultsDescription,
}: TableParams<T>) => {
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>(search);
  const [filter, setFilter] = useState<Filter>(Filter.ALL);

  useEffect(() => {
    setFilter(hasPreselectedConfig ? Filter.ADDED : Filter.ALL);
  }, [hasPreselectedConfig]);

  const { sortOrder, sortBy, getSortableColumnProps } = useSortableTable({
    initialSortBy: null,
  });

  const filteredTables = useMemo(() => {
    return filter === Filter.ALL
      ? dataSource
      : dataSource.filter(t => selectedTables[t.id]);
  }, [dataSource, filter, selectedTables]);

  const sortedTables = useMemo(() => {
    return smartSort(filteredTables, sortBy, sortOrder);
  }, [filteredTables, sortBy, sortOrder]);

  const sortedSearchTables = useMemo(() => {
    return filterTables(debouncedSearch, sortedTables);
  }, [debouncedSearch, sortedTables]);

  const handleDebounceSearch = useMemo(
    () =>
      debounce(500, (value: string) => {
        setDebouncedSearch(value);
      }),
    []
  );

  const handleOnSearch = (value: string) => {
    setSearch(value);
    handleDebounceSearch(value);
  };

  return (
    <div style={disabled ? { opacity: 0.5 } : {}} ref={selectionRef}>
      {/* If you provide an empty string explicitly that means no label */}
      {texts.searchLabel && <Header>{texts.searchLabel}</Header>}
      {texts.searchText && <Text>{texts.searchText}</Text>}
      <TableSearch
        placeholder={texts.searchPlaceholder}
        search={search}
        onSearch={handleOnSearch}
      />
      <FlexBox justify="space-between" align="center">
        <Paragraph variant="caption">
          {Object.keys(selectedTables).length} / {dataSource.length}{' '}
          {texts.selectedTitle}
        </Paragraph>
        <FilterContainer>
          <SegmentedControl
            items={[
              {
                id: Filter.ALL,
                label: allFilterLabel,
                icon: IconName.TABLE_ROWS,
              },
              {
                id: Filter.ADDED,
                label: addedFilterLabel,
                icon: IconName.CHECKBOX_CHECKED,
              },
            ]}
            selectedId={filter}
            onSelectionChange={setFilter}
          />
        </FilterContainer>
      </FlexBox>
      <TablesContainer>
        <TablesTable
          i18nLocale={i18nLocale}
          selectedTables={selectedTables}
          dataSource={sortedSearchTables}
          texts={texts}
          selectedRowId={selectedRowId}
          onCellClick={row => onSelectTable(row)}
          getSortableColumnProps={getSortableColumnProps}
          noResultsTitle={noResultsTitle}
          noResultsDescription={noResultsDescription}
        />
      </TablesContainer>
    </div>
  );
};
